import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { LanguageService } from '@mzic/mzic-services';
import { AccordionComponent } from '../../components/accordion/accordion.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';

@Component({
  selector: 'mzic-market',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AccordionComponent,
    TranslocoModule,
    RouterModule,
  ],
  templateUrl: './market.component.html',
  styleUrl: './market.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'market',
      alias: 'marketLang',
    }),
  ],
})
export class MarketComponent implements OnInit, AfterViewInit {
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);

  activeAccordion: string | null = null;
  showRightButton = true;
  showLeftButton = false;

  @ViewChild('carouselContainer') carouselContainer!: ElementRef;
  @ViewChild('industrySizeAccordion', { read: ElementRef })
  industrySizeAccordion!: ElementRef;
  @ViewChild('howMusicGeneratesRevenueAccordion', { read: ElementRef })
  howMusicGeneratesRevenueAccordion!: ElementRef;
  @ViewChild('whyInvestInMusicAccordion', { read: ElementRef })
  whyInvestInMusicAccordion!: ElementRef;

  revenueCards = [
    {
      type: 'badges',
      key: 'streamingDigital',
      badges: [
        {
          icon: 'assets/icons/mzic-spotify-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.spotify',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/youtube-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.youtubeMusic',
        },
        {
          icon: 'assets/icons/mzic-apple-music-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.appleMusic',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/amazon-music-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.amazonMusic',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/instagram-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.instagram',
        },
        {
          icon: 'https://mzic-website.s3.us-east-1.amazonaws.com/tik-tok-icon.svg',
          textKey:
            'marketLang.accordions.howMusicGeneratesRevenue.content.cards.streamingDigital.badges.tiktok',
        },
      ],
    },
    {
      type: 'icons',
      key: 'publicExecution',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/radio-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/restaurant-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/gym-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/bag-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/club-icon.svg',
      ],
    },
    {
      type: 'icons',
      key: 'liveMusic',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/microphone-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/transmission-icon.svg',
      ],
    },
    {
      type: 'icons',
      key: 'synchronization',
      icons: [
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-cut-scene-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-controller-icon.svg',
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-tv-icon.svg',
      ],
    },
  ];

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();

      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
  }

  ngAfterViewInit() {
    this.checkScrollPosition();
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  toggleAccordion(id: string) {
    // Se está clicando no mesmo accordion, apenas fecha
    if (this.activeAccordion === id) {
      this.activeAccordion = null;
      return;
    }

    // Se há um accordion aberto e está clicando em outro
    if (this.activeAccordion && this.activeAccordion !== id) {
      // Primeiro fecha o atual
      this.activeAccordion = null;

      // Aguarda a animação de fechamento antes de processar o novo
      setTimeout(() => {
        this.openAccordionWithScroll(id);
      }, 600);
    } else {
      // Se nenhum está aberto, abre direto
      this.openAccordionWithScroll(id);
    }
  }

  openAccordionWithScroll(id: string) {
    const accordionElement = this.getAccordionElement(id);

    if (!accordionElement) {
      this.activeAccordion = id;
      return;
    }

    const element = accordionElement.nativeElement;
    const rect = element.getBoundingClientRect();
    const offset = 120;

    // Verifica se o título do accordion está visível
    const isVisible = rect.top >= offset && rect.top <= window.innerHeight;

    if (!isVisible) {
      // Se não está visível, scrolla primeiro
      const elementPosition = rect.top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Aguarda o scroll terminar antes de abrir
      setTimeout(() => {
        this.activeAccordion = id;
        setTimeout(() => this.checkScrollPosition(), 100);
      }, 400);
    } else {
      // Se já está visível, abre direto
      this.activeAccordion = id;
      setTimeout(() => this.checkScrollPosition(), 100);
    }
  }

  getAccordionElement(id: string): ElementRef | undefined {
    switch (id) {
      case 'industrySize':
        return this.industrySizeAccordion;
      case 'howMusicGeneratesRevenue':
        return this.howMusicGeneratesRevenueAccordion;
      case 'whyInvestInMusic':
        return this.whyInvestInMusicAccordion;
      default:
        return undefined;
    }
  }

  checkScrollPosition() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // No início
      this.showLeftButton = scrollLeft > 10;

      // No final (com margem de 10px de tolerância)
      this.showRightButton = scrollLeft < scrollWidth - clientWidth - 10;
    }
  }

  scrollCarousel() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const card = container.querySelector('.revenue-card') as HTMLElement;

      if (card) {
        const cardWidth = card.offsetWidth;
        const gap = window.innerWidth <= 768 ? 16 : 26;
        const scrollAmount = cardWidth + gap;

        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }

      setTimeout(() => this.checkScrollPosition(), 400);
    }
  }

  scrollCarouselBack() {
    if (this.carouselContainer) {
      const container = this.carouselContainer.nativeElement;
      const card = container.querySelector('.revenue-card') as HTMLElement;

      if (card) {
        const cardWidth = card.offsetWidth;
        const gap = window.innerWidth <= 768 ? 16 : 26;
        const scrollAmount = cardWidth + gap;

        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      }

      setTimeout(() => this.checkScrollPosition(), 400);
    }
  }
}
